import { useCallback, useMemo, useState } from 'react'
import { MapMarker, MapView, tangerRasterSource, MAP_MAX_ZOOM, MAP_MIN_ZOOM, type MapViewport } from '@wehunt/map'
import { DEFAULT_MAP_APPEARANCE } from '../../lib/mapAppearance'
import { pointsToBBox } from '../../lib/mapSeedView'
import { useBaseMapTileInfo } from '../../lib/useBaseMapTileInfo'
import type { GeoLine, GeoPt, MapObject, TeamMap } from '../../lib/types'
import { PinMarkerContent } from '../PinMarkerContent'
import { TeamMapLayers } from '../TeamMapLayers'

/** How close a single shared pin opens: near enough to read the terrain around it. */
export const SHARED_PIN_ZOOM = 14

export type SharedMapObject =
  | { kind: 'pin'; pin: Omit<MapObject, 'id'> }
  | { kind: 'line'; line: Omit<GeoLine, 'id'> }

export interface SharedMapObjectMapProps {
  object: SharedMapObject
  /** Accessible name for the map region. */
  label: string
}

const SHARED_ID = 'shared'

/**
 * One shared pin or line on the base map, drawn exactly as the team map draws
 * it — the same pin art and the same line layers — so an object shared from the
 * app looks here the way it looks on the team's map.
 *
 * Presentation only: the caller resolves the link and hands over the object.
 * The base map is anonymous-friendly (`/api/maps` answers signed out), which is
 * what lets a public page show it.
 */
export function SharedMapObjectMap({ object, label }: SharedMapObjectMapProps) {
  const tileInfo = useBaseMapTileInfo()
  const baseSource = useMemo(
    () => (tileInfo ? tangerRasterSource({ ...tileInfo, widths: [] }) : null),
    [tileInfo],
  )
  const [zoom, setZoom] = useState(SHARED_PIN_ZOOM)
  const onViewChange = useCallback((view: MapViewport) => setZoom(view.zoom), [])

  const pin = object.kind === 'pin' ? { ...object.pin, id: SHARED_ID } : null
  const line = object.kind === 'line' ? object.line : null

  // A stable map for the layer stack: only the line changes it, and a fresh
  // object per render would re-run every layer's geometry.
  const lineMap = useMemo<TeamMap | null>(
    () =>
      line === null
        ? null
        : { name: '', updated: '', boundaryAreas: [], objects: [], lines: [{ ...line, id: SHARED_ID }] },
    [line],
  )
  const initialBounds = useMemo(
    () => (line === null ? null : pointsToBBox(line.points as readonly GeoPt[])),
    [line],
  )
  const initialView = pin === null ? undefined : { center: { lat: pin.lat, lon: pin.lon }, zoom: SHARED_PIN_ZOOM }

  return (
    <div role="region" aria-label={label} style={{ position: 'absolute', inset: 0 }}>
      {!tileInfo && <div style={{ position: 'absolute', inset: 0, background: '#e8efdf' }} />}
      <MapView
        baseSource={baseSource}
        initialBounds={initialBounds}
        initialView={initialView}
        interactive
        minZoom={MAP_MIN_ZOOM}
        maxZoom={MAP_MAX_ZOOM}
        onViewChange={onViewChange}
      >
        {lineMap !== null && (
          <TeamMapLayers map={lineMap} appearance={DEFAULT_MAP_APPEARANCE} zoom={zoom} subAreaLabels={false} />
        )}
        {pin !== null && (
          <MapMarker lat={pin.lat} lon={pin.lon} anchor="bottom">
            <PinMarkerContent object={pin} appearance={DEFAULT_MAP_APPEARANCE} zoom={zoom} pinNamesVisible />
          </MapMarker>
        )}
      </MapView>
    </div>
  )
}
